import clsx from "clsx";
import { forwardRef } from "react";

import type { TypoStyle } from "../Typo";

import { FieldError } from "../FieldError";
import { Typo } from "../Typo";

type InputProps = {
  name: string;
  value: string;
  label?: string;
  labelType?: TypoStyle;
  smLabelType?: TypoStyle;
  subLabel?: string;
  disabled?: boolean;
  type?: string;
  error?: string;
  showError?: boolean;
  maxLength?: number;
  placeholder?: string;
  characterLimit?: number;
  /**
   * Accessible name for fields rendered without a visible `label` — e.g. a
   * modal whose heading already names the field. Present on the DS source
   * component; without it a label-less field has no accessible name at all.
   */
  ariaLabel?: string;
  "data-testid"?: string;
  onChange: (value: string) => void;
  onFocus?: () => void;
};

export const InputV2 = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      name,
      label,
      labelType = "labelMD",
      smLabelType = "labelSM",
      subLabel,
      value,
      disabled = false,
      type = "text",
      error,
      showError = true,
      maxLength,
      placeholder,
      characterLimit,
      ariaLabel,
      "data-testid": testId,
      onChange,
      onFocus,
    },
    ref,
  ) => {
    const inputClasses = clsx(
      "h-10",
      "p-2",
      "rounded-lg",
      "outline-none",
      "border-none",
      "font-body",
      "text-sm",
      "w-full",
      disabled
        ? "cursor-not-allowed bg-surface-disabled text-text-disabled"
        : "bg-surface-tonal-default text-text-primary hover:bg-surface-tonal-bold focus:ring-2 focus:ring-stroke-focused",
      error && "ring-2 ring-text-error",
    );

    const showLabel = label || subLabel;

    return (
      <div className="flex w-full flex-col gap-2">
        {showLabel && (
          <div className="flex flex-col gap-1">
            {label && (
              <label htmlFor={name}>
                <Typo element="p" type={labelType} smType={smLabelType}>
                  {label}
                </Typo>
              </label>
            )}
            {subLabel && (
              <Typo element="p" type="bodyXS" textColor="text-text-secondary">
                {subLabel}
              </Typo>
            )}
          </div>
        )}

        <div className="relative w-full">
          <input
            ref={ref}
            id={name}
            name={name}
            disabled={disabled}
            type={type}
            value={value}
            aria-label={ariaLabel}
            data-testid={testId}
            onChange={(e) => onChange(e.target.value)}
            className={clsx(inputClasses, characterLimit && "pr-16")}
            maxLength={maxLength ?? characterLimit}
            placeholder={placeholder}
            onFocus={onFocus}
          />
          {characterLimit && (
            <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-xs text-text-secondary">
              {(value ?? "").length}/{characterLimit}
            </span>
          )}
        </div>
        {showError && error && (
          <FieldError text={error} className="text-text-error" />
        )}
      </div>
    );
  },
);

InputV2.displayName = "InputV2";
